import React, { useState, useEffect } from 'react';
import { useAuth } from '../../components/auth/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { updateProfile, changePassword } from '../../utils/api';
import Seo from '../common/Seo';

const Profile = () => {
  const { user, updateUserData } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    club: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name,
        club: user.club
      }));
    }
  }, [user]);

  if (!user) {
    return <Navigate to="/login" />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      const response = await updateProfile({
        name: formData.name,
        club: formData.club
      });

      if (response.success && response.data) {
        updateUserData(response.data);
        setSuccess('Profil erfolgreich aktualisiert');
        setIsEditing(false);
      } else {
        setError(response.message || 'Ein Fehler ist aufgetreten');
      }
    } catch (err) {
      setError('Ein Fehler ist aufgetreten');
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Die neuen Passwörter stimmen nicht überein');
      return;
    }

    if (!formData.currentPassword || !formData.newPassword) {
      setError('Bitte alle Passwort-Felder ausfüllen');
      return;
    }

    try {
      const response = await changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (response.success) {
        setSuccess('Passwort erfolgreich geändert');
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
        setIsEditing(false);
      } else {
        setError(response.message || 'Fehler beim Ändern des Passworts');
      }
    } catch (err) {
      setError('Ein Fehler ist aufgetreten');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <Seo title="Mein Profil | SportyReplay" noindex />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Willkommensbereich */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg shadow-xl p-8 mb-8">
          <h1 className="text-3xl font-bold mb-3">Willkommen bei SportyReplay, {user.name}!</h1>
          <p className="text-lg mb-6">
            Deine Plattform für den Kauf und Verkauf von gebrauchten Sportartikeln im Vereinsumfeld.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link 
              to="/" 
              className="bg-white text-orange-600 px-5 py-2 rounded-lg font-medium hover:bg-gray-100 transition"
            >
              Artikel entdecken
            </Link>
          </div>
        </div>

        {/* Schnellzugriff-Karten */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition">
            <div className="text-3xl text-orange-500 mb-2">
              <i className="fas fa-tshirt"></i>
            </div>
            <h2 className="text-xl font-semibold mb-2">Meine Angebote</h2>
            <p className="text-gray-600 mb-4">Verwalte deine eingestellten Artikel.</p>
            <Link to="/my-products" className="text-orange-500 font-medium hover:underline">
              Zu meinen Angeboten
            </Link>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition">
            <div className="text-3xl text-orange-500 mb-2">
              <i className="fas fa-comments"></i>
            </div>
            <h2 className="text-xl font-semibold mb-2">Nachrichten</h2>
            <p className="text-gray-600 mb-4">Kommuniziere mit Käufern und Verkäufern.</p>
            <Link to="/messages" className="text-orange-500 font-medium hover:underline">
              Zu den Nachrichten
            </Link>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition">
            <div className="text-3xl text-orange-500 mb-2">
              <i className="fas fa-cog"></i>
            </div>
            <h2 className="text-xl font-semibold mb-2">Einstellungen</h2>
            <p className="text-gray-600 mb-4">Bearbeite deine Profildaten.</p>
            <button 
              onClick={() => setIsEditing(true)} 
              className="text-orange-500 font-medium hover:underline"
            >
              Profil bearbeiten
            </button>
          </div>
        </div>

        {/* Profil-Bereich */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Profil</h1>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
              >
                Profil bearbeiten
              </button>
            )}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Profil Informationen</h2>

            {isEditing ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={user.email}
                    disabled
                    className="w-full p-3 border border-gray-200 rounded bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-2">Verein</label>
                  <input
                    type="text"
                    name="club"
                    value={formData.club}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Passwort ändern</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 mb-2">Aktuelles Passwort</label>
                      <input
                        type="password"
                        name="currentPassword"
                        value={formData.currentPassword}
                        onChange={handleChange}
                        className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 mb-2">Neues Passwort</label>
                      <input
                        type="password"
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleChange}
                        className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 mb-2">Neues Passwort bestätigen</label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handlePasswordChange}
                        className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                      >
                        Passwort ändern
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                  >
                    Speichern
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-500 text-sm">Name</label>
                  <p className="text-gray-900">{user.name}</p>
                </div>
                <div>
                  <label className="block text-gray-500 text-sm">Email</label>
                  <p className="text-gray-900">{user.email}</p>
                </div>
                <div>
                  <label className="block text-gray-500 text-sm">Verein</label>
                  <p className="text-gray-900">{user.club}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;